import { useContext, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { getPanelTransactions } from 'src/redux/panel_transactions/thunks';
import { buildQueryKey } from 'src/redux/panel_transactions/panel_transactions.constants';
import {
    selectPanelTransactionsLoadedForKey,
    selectPanelTransactionsQuery,
} from 'src/redux/panel_transactions/panel_transactions.selectors';
import { RetentionSearchContext } from '../search';

/**
 * Carga perezosa del listado de transacciones (`commercial_panel/transactions`).
 *
 * El correo del listado (`query.customerEmail`) es la fuente de verdad de esta
 * pestaña: filtra GET commercial_panel/transactions y no pisa carrito/vigencias.
 *
 * El buscador central solo SIEMBRA ese filtro cuando trae un correo nuevo (quien
 * ya buscó un cliente en otra tab no tiene que volver a tipear). Un buscador
 * central vacío no borra un filtro que se puso solo en el listado: si no, al
 * volver a la tab se perdería.
 *
 * El guard compara la firma de la consulta pedida contra la última cargada, así
 * que alternar de pestaña no vuelve a pegarle al servicio.
 */
export const useLazyPanelTransactions = () => {
    const dispatch = useAppDispatch();
    const { submittedQuery } = useContext(RetentionSearchContext);
    const query = useAppSelector(selectPanelTransactionsQuery);
    const loadedForKey = useAppSelector(selectPanelTransactionsLoadedForKey);

    const seenSubmitted = useRef<string | undefined>(undefined);
    const pendingSeed = useRef<string | null>(null);

    if (seenSubmitted.current !== submittedQuery) {
        seenSubmitted.current = submittedQuery;
        if (submittedQuery !== '') {
            pendingSeed.current = submittedQuery;
        }
    }

    if (pendingSeed.current !== null && query.customerEmail === pendingSeed.current) {
        pendingSeed.current = null;
    }

    const customerEmail =
        pendingSeed.current !== null ? pendingSeed.current : query.customerEmail;

    const isCustomerInSync = query.customerEmail === customerEmail;
    const targetKey = buildQueryKey({
        ...query,
        customerEmail,
        page: isCustomerInSync ? query.page : 0,
    });

    useEffect(() => {
        if (loadedForKey === targetKey) return;
        dispatch(getPanelTransactions({ customerEmail }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetKey]);

    return { submittedQuery, isLoaded: loadedForKey === targetKey };
};
